import { useCallback, useEffect, useState } from "react";

// Shared row-density preference used by Transactions, Reconciliation,
// Statements, and the Review Queue. One dial controls them all so a user
// who likes compact rows gets compact everywhere. Persisted to
// localStorage and synced within-tab via a custom event (storage events
// only fire cross-tab).
const STORAGE_KEY = "ll_row_density";
const EVENT_NAME = "ll:row-density-change";

export const DENSITY_PRESETS = {
  compact:      { tdY: "py-1",   txt: "text-xs" },
  cozy:         { tdY: "py-2.5", txt: "text-sm" },
  comfortable:  { tdY: "py-4",   txt: "text-sm" },
};

const readSaved = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && DENSITY_PRESETS[v]) return v;
  } catch { /* ignore */ }
  return "cozy";
};

export function useRowDensity() {
  const [density, setDensity] = useState(readSaved);

  useEffect(() => {
    const sync = () => setDensity(readSaved());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) sync(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, []);

  const setDensityPref = useCallback((next) => {
    if (!DENSITY_PRESETS[next]) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    try { window.dispatchEvent(new Event(EVENT_NAME)); } catch { /* ignore */ }
    setDensity(next);
  }, []);

  return { density, setDensity: setDensityPref, cfg: DENSITY_PRESETS[density] || DENSITY_PRESETS.cozy };
}
